import { DynamicQuestion } from '@common/dynamic-form';
import { heraldQuoteExitTriggerQuestions } from '@common/types';
import { isAnswerEmpty } from '@common/utils';
import { useEffect, useMemo, useState } from 'react';

export enum FormMode {
  Minimal = 'Minimal',
  Full = 'Full',
}

interface GetDynamicFormDisplayProps {
  isHeraldSubmission: boolean;
  isFirstVisit?: boolean;
  isLoading: boolean;
  potentialDynamicQuestions: DynamicQuestion[];
  vipQuestions: string[];
}

const isQuestionEmpty = (question: DynamicQuestion) => {
  const isMainRequiredQuestionEmpty = question.requiredForCompletion && isAnswerEmpty(question.value);
  const isChildRequiredQuestionEmpty = question.childValues?.some(isQuestionEmpty);

  return isMainRequiredQuestionEmpty || isChildRequiredQuestionEmpty;
};

export function useGetDynamicFormDisplayProps({
  isHeraldSubmission,
  isLoading,
  isFirstVisit,
  potentialDynamicQuestions,
  vipQuestions,
}: GetDynamicFormDisplayProps) {
  const [formMode, setFormMode] = useState<FormMode | undefined>();

  const minimalFormQuestions = useMemo(
    () =>
      potentialDynamicQuestions.filter(
        (dynamicQuestion) =>
          vipQuestions.includes(dynamicQuestion.id) || heraldQuoteExitTriggerQuestions.includes(dynamicQuestion.id),
      ),
    [potentialDynamicQuestions, vipQuestions],
  );

  useEffect(() => {
    // set formMode only once when we have all the information (in certain case we also need to wait for loading to finish)
    if (!formMode) {
      if (!isHeraldSubmission) {
        setFormMode(FormMode.Full);
      } else if (isFirstVisit) {
        setFormMode(FormMode.Minimal);
      } else if (!isLoading) {
        // if all required minimalFormQuestions questions are answered set to Full otherwise Minimal
        const requiredMinimalFormQuestionEmpty = minimalFormQuestions.some(isQuestionEmpty);
        setFormMode(requiredMinimalFormQuestionEmpty ? FormMode.Minimal : FormMode.Full);
      }
    }
  }, [isFirstVisit, isHeraldSubmission, isLoading, potentialDynamicQuestions, formMode, minimalFormQuestions]);

  const filteredQuestions = useMemo(() => {
    switch (formMode) {
      case FormMode.Minimal:
        return minimalFormQuestions;
      case FormMode.Full:
        return potentialDynamicQuestions;
      default:
        return [];
    }
  }, [formMode, potentialDynamicQuestions, minimalFormQuestions]);

  return {
    formMode,
    setFormMode,
    dynamicQuestions: filteredQuestions,
  };
}
